import type { ProjectSchema } from '@readme/backend/models/project/types';
import type { User } from 'oas/types';

import { plain, hast } from '@readme/markdown';

/*
 * Merges user variables with default variables
 * and returns a single object with all variables.
 */
function mergeVariables({ user, defaults }: { user?: User; defaults?: { name: string; default?: string }[] }) {
  const defaultsMap: Record<string, string> = defaults
    ? Object.fromEntries(defaults.map(({ name, default: def }) => [name, def ?? '']))
    : {};

  return {
    ...defaultsMap,
    ...user,
  };
}

/**
 * Renders MDX content to plain text, applying variable substitutions.
 * Used for generating plain text summaries of MDX content for search indexing or
 * metadata extraction.
 */
export function renderMdxPlain(
  mdx: string,
  // These are both properties of the request object
  variables: {
    user?: User;
    defaults?: ProjectSchema['variableDefaults'];
  },
  { truncationLength }: { truncationLength?: number } = {},
): ReturnType<typeof plain> {
  let rmdxHast: ReturnType<typeof hast>;
  try {
    rmdxHast = hast(mdx);
  } catch (e) {
    // If the MDX is invalid, return an empty string
    return '';
  }

  // Cast to Record<string, string> for mdx type compatibility
  const mergedVariables = mergeVariables(variables) as Record<string, string>;

  // If no truncation length is provided, return the full text
  if (!truncationLength) {
    return plain(rmdxHast, { variables: mergedVariables });
  }

  let text = '';
  let i = 0;
  let wasTruncated = false;

  // Iterate through the children of the hast node
  // and concatenate the text until the truncation length is reached
  while (text.length < truncationLength && i < rmdxHast.children.length) {
    const rawChunk = plain(rmdxHast.children[i], {
      variables: mergedVariables,
    });

    const chunk = String(rawChunk);
    const remaining = truncationLength - text.length;

    // If the next chunk pushes us over the truncation length, truncate it
    if (chunk.length > remaining) {
      text += chunk.slice(0, remaining);
      wasTruncated = true;
      break;
    }

    text += chunk;
    i += 1;
  }

  if (wasTruncated) {
    text += '…';
  }

  return text;
}
